import { useMemo } from 'react';
import {
  Box,
  ChevronsUpDown,
  FlaskConical,
  Plus,
  Settings,
} from 'lucide-react';

import { Dialog, Skeleton, useDialog } from '@components/primitives';

import { AppIcon } from '@components/features/Apps/AppIcon';
import { Command } from '@components/features/CommandMenu';
import { CreateAppDialogContent } from '@components/features/Layout/Navigation/AppDropdown/CreateAppDialog';
import { NavigationDropdown } from '@components/features/Layout/Navigation/Dropdown';
import { useTeamAppSwitch } from '@components/features/Layout/Navigation/navigate';
import { SlantedButton } from '@components/features/Layout/Navigation/SlantedButton';
import { Restricted } from '@components/features/Restricted';
import { useAppQuery, useAppsQuery } from '@queries/app';

import css from './styles.module.css';

interface AppLockupProps {
  teamUuid: string;
  appUuid: string;
}

export function AppLockup({ teamUuid, appUuid }: AppLockupProps) {
  const { data: app } = useAppQuery(teamUuid, appUuid);
  return (
    <span className={css.lockup}>
      <span className={css.icon} aria-hidden>
        <AppIcon app={app} />
      </span>
      <span className={css.name}>
        {app?.name ?? <Skeleton width={96} height={12} />}
      </span>
    </span>
  );
}
export interface AppDropdownProps {
  teamUuid: string;
  appUuid: string;
}

export function AppDropdown({ teamUuid, appUuid }: AppDropdownProps) {
  const { getAppPath } = useTeamAppSwitch(teamUuid);

  const { data: app } = useAppQuery(teamUuid, appUuid);
  const { data: apps } = useAppsQuery(teamUuid);

  const otherApps = useMemo(() => {
    return apps?.filter((app) => app.uuid !== appUuid) ?? [];
  }, [apps, appUuid]);

  const createAppDialog = useDialog();

  return (
    <>
      <NavigationDropdown
        trigger={
          <SlantedButton suffix={<ChevronsUpDown />}>
            <AppLockup teamUuid={teamUuid} appUuid={appUuid} />
          </SlantedButton>
        }
      >
        <NavigationDropdown.Section>
          <NavigationDropdown.Item
            icon={app?.sandbox ? <FlaskConical /> : <Box />}
            label={
              app ? (
                <span className={css.appName}>{app.name}</span>
              ) : (
                <Skeleton width={96} height={12} />
              )
            }
          />
          <NavigationDropdown.Item
            icon={<Settings />}
            label="Settings"
            to={`/${teamUuid}/${appUuid}/settings`}
          />
        </NavigationDropdown.Section>

        {otherApps.length > 0 && (
          <NavigationDropdown.Section label="Switch app">
            {otherApps.map((app) => (
              <NavigationDropdown.Item
                key={app.uuid}
                icon={app?.sandbox ? <FlaskConical /> : <Box />}
                label={app.name}
                to={getAppPath(app)}
              />
            ))}
          </NavigationDropdown.Section>
        )}

        <Restricted
          allow="devOrAdmin"
          teamUuid={teamUuid}
          render={<NavigationDropdown.Section dimmed />}
        >
          <NavigationDropdown.Item
            icon={<Plus />}
            label="Create new app"
            onClick={createAppDialog.open}
          />
        </Restricted>
      </NavigationDropdown>

      <Dialog state={createAppDialog}>
        <CreateAppDialogContent teamUuid={teamUuid} />
      </Dialog>

      {otherApps.map((app) => (
        <Command
          key={app.uuid}
          type="route"
          id={`navigate-to-${app.uuid}`}
          label={app.name}
          group="Switch app"
          icon={<Box />}
          href={getAppPath(app)}
          context="team"
          searchable="only"
        />
      ))}

      <Restricted
        allow="devOrAdmin"
        teamUuid={teamUuid}
        render={
          <Command
            type="action"
            id="create-app"
            label="Create new app"
            icon={<Plus />}
            context="team"
            onSelect={createAppDialog.open}
            group="Switch app"
            keywords={['new app']}
            searchable="only"
          />
        }
      />
    </>
  );
}
